/**
 * PROVA IMPRESSA — o percurso em papel.
 *
 * As mesmas questões, geradas pelo mesmo motor, numa página feita para imprimir: cada
 * interação vira o seu equivalente no papel.
 *  - alternativas: (A) a (E);
 *  - resposta digitada: uma linha;
 *  - expressão: a linha depois de "aₙ =" ou "f(x) =";
 *  - ficha de coordenadas: a ficha, com os pares a completar;
 *  - construção e marcação: a figura, para construir ou marcar a lápis;
 *  - arrastar e marcar regiões: a figura, com a instrução de desenhar ou marcar com X;
 *  - ordenar: os cartões, para numerar;
 *  - encontre o erro: os passos, para circular;
 *  - figura animada ou com controles: o quadro parado, com uma nota;
 *  - mapa com régua: a régua desenhada na MESMA escala, para copiar numa tira de papel.
 *
 * O gabarito comentado é opcional e vem em página separada, para o professor.
 *
 * A página força o tema claro enquanto está aberta: tinta clara em papel branco some.
 */

import { formatBR } from '../core/num.ts';
import { nunca } from '../core/nunca.ts';
import { capitalizar, conta, lista, nome, numeroDe, umA } from '../core/pt.ts';
import { evaluate } from '../geom/construction.ts';
import { constructionToScene } from '../render/geom-scene.ts';
import { balanca } from '../render/balanca.ts';
import { scene, type Scene, type Shape } from '../render/scene.ts';
import { renderScene } from '../render/svg.ts';
import type {
  ConstructInteraction,
  ItemInstance,
  MeasurableFigure,
  Stimulus,
} from '../schema/item.ts';
import { toPlainText, type RichText } from '../schema/richtext.ts';
import { renderRichInline, renderRichText } from '../ui/richtext.ts';

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  cls?: string,
  texto?: string,
): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (texto !== undefined) e.textContent = texto;
  return e;
}

function figura(cena: Scene, nota?: string): HTMLElement {
  const f = el('figure', 'prova-figura');
  f.appendChild(renderScene(cena));
  if (nota) f.appendChild(el('p', 'prova-nota', nota));
  return f;
}

/** A régua do estímulo, desenhada na escala da figura: no papel, ela é copiável. */
export function reguaComoFormas(spec: MeasurableFigure['regua']): Shape[] {
  const E = spec.escala;
  const L = spec.comprimento * E;
  const sobra = 0.55 * E;
  const larg = 0.9 * E;
  const { x: x0, y: y0 } = spec.inicio;
  const formas: Shape[] = [
    {
      t: 'rect',
      at: { x: x0 - sobra, y: y0 - larg, w: L + 2 * sobra, h: larg },
      role: 'given',
      fill: 'surface',
    },
  ];
  const sub = spec.subdivisoes;
  for (let i = 0; i <= spec.comprimento * sub; i++) {
    const x = x0 + (i * E) / sub;
    const inteiro = i % sub === 0;
    const meio = sub % 2 === 0 && i % (sub / 2) === 0;
    formas.push({
      t: 'seg',
      a: { x, y: y0 },
      b: { x, y: y0 - larg * (inteiro ? 0.42 : meio ? 0.3 : 0.18) },
      role: inteiro ? 'given' : 'auxiliary',
    });
    if (inteiro)
      formas.push({
        t: 'label',
        at: { x, y: y0 - larg * 0.72 },
        text: [{ t: 'text', s: String(i / sub) }],
        anchor: 'c',
        emphasis: 'tick',
      });
  }
  return formas;
}

function tabela(s: Extract<Stimulus, { kind: 'table' }>): HTMLElement {
  const t = el('table', 'prova-tabela');
  const trh = el('tr');
  for (const h of s.headers) {
    const th = el('th');
    th.appendChild(renderRichInline(h));
    trh.appendChild(th);
  }
  const thead = el('thead');
  thead.appendChild(trh);
  const tbody = el('tbody');
  for (const linha of s.rows) {
    const tr = el('tr');
    for (const c of linha) {
      const td = el('td');
      td.appendChild(renderRichInline(c));
      tr.appendChild(td);
    }
    tbody.appendChild(tr);
  }
  t.append(thead, tbody);
  return t;
}

function estimulo(s: Stimulus): HTMLElement {
  switch (s.kind) {
    case 'static-figure':
      return figura(s.scene);
    case 'animated':
      return figura(s.frame(s.staticFallbackAt), 'Na versão digital, esta figura é animada.');
    case 'manipulable': {
      const v = s.quadroImpresso ?? Object.fromEntries(s.controls.map((c) => [c.id, c.initial]));
      const nomes = s.controls.map((c) => toPlainText(c.label)).join(', ');
      const nota = s.quadroImpresso
        ? 'Na versão digital, esta figura muda com um controle; aqui, ela aparece já na posição em que é usada.'
        : `Na versão digital, esta figura muda com ${numeroDe(s.controls.length) === 'sg' ? umA('controle') : nome('controle', 'pl')} (${nomes}).`;
      return figura(s.frame(v), nota);
    }
    case 'measurable':
      return figura(
        scene([...s.scene.shapes, ...reguaComoFormas(s.regua)], {
          viewBox: s.scene.viewBox,
          a11y: s.scene.a11y,
        }),
        'A régua abaixo do mapa está na escala do desenho: copie as marcas numa tira de papel para medir.',
      );
    case 'table':
      return tabela(s);
  }
}

function cenaDaConstrucao(inter: ConstructInteraction): Scene {
  const base = constructionToScene(evaluate(inter.givens), {
    title: 'Figura da questão',
    desc: 'Figura da questão, para construir ou marcar a lápis.',
    givenIds: new Set(inter.givens.order.map((g) => g as string)),
    ...(inter.viewBox ? { viewBox: inter.viewBox } : {}),
  });
  return scene([...(inter.backdrop ?? []), ...base.shapes], {
    viewBox: base.viewBox,
    a11y: base.a11y,
  });
}

const linha = (rotulo: string, largura = 16): HTMLElement => {
  const p = el('p', 'prova-linha');
  p.append(document.createTextNode(`${rotulo} `), el('span', 'prova-branco', ' '.repeat(largura)));
  return p;
};

function resposta(inst: ItemInstance): HTMLElement {
  const box = el('div', 'prova-resposta');
  const inter = inst.interaction;
  switch (inter.kind) {
    case 'select-one': {
      // Alternativas que são figuras (estrutura E6) vão em grade: cinco gráficos
      // empilhados gastariam uma página inteira.
      const ol = el(
        'ol',
        inter.alternatives.some((a) => a.figure)
          ? 'prova-alternativas com-figuras'
          : 'prova-alternativas',
      );
      for (const a of inter.alternatives) {
        const li = el('li');
        li.appendChild(el('span', 'prova-letra', `(${a.id})`));
        const corpo = el('span', 'prova-alt-corpo');
        corpo.appendChild(renderRichInline(a.content));
        if (a.figure) corpo.appendChild(renderScene(a.figure));
        li.appendChild(corpo);
        ol.appendChild(li);
      }
      box.appendChild(ol);
      break;
    }
    case 'numeric-entry':
      box.appendChild(linha('Resposta:'));
      if (inter.unit) box.lastElementChild?.appendChild(document.createTextNode(` ${inter.unit}`));
      break;
    case 'expression-entry':
      box.appendChild(linha(inter.prefixo, 24));
      break;
    case 'coordinate-entry': {
      // Na tela, os nomes e as coordenadas da ficha são HTML por cima do desenho; no
      // papel, entram no próprio desenho — com as coordenadas conhecidas escritas.
      const rotulos: Shape[] = inter.pontos.map((p) => ({
        t: 'label',
        at: p.em,
        text: [
          {
            t: 'text',
            s: p.valor ? `${p.nome} (${formatBR(p.valor.x)}, ${formatBR(p.valor.y)})` : p.nome,
          },
        ],
        anchor: p.lado,
      }));
      box.appendChild(
        figura(
          scene([...inter.ficha.shapes, ...rotulos], {
            viewBox: inter.ficha.viewBox,
            a11y: inter.ficha.a11y,
          }),
        ),
      );
      const faltam = inter.pontos.filter((p) => !p.valor).map((p) => `${p.nome} = ( ____ , ____ )`);
      box.appendChild(el('p', 'prova-linha', faltam.join('     ')));
      break;
    }
    case 'construct':
      if (!inst.stimuli.some((s) => s.kind === 'static-figure'))
        box.appendChild(figura(cenaDaConstrucao(inter)));
      break;
    case 'drag-points': {
      const fixos = new Set(inter.fixos ?? []);
      const pontos: Shape[] = inter.pontos.flatMap((p, i) =>
        fixos.has(i) ? [{ t: 'point', p: p.inicial, role: 'given', name: p.nome } as Shape] : [],
      );
      box.appendChild(
        figura(
          scene([...inter.backdrop, ...pontos], {
            viewBox: inter.viewBox,
            a11y: { title: 'Figura da questão', desc: inter.descricao },
          }),
        ),
      );
      break;
    }
    case 'balance-ops': {
      // No papel a balança é um desenho e uma tabela de etapas: o aluno escreve o que
      // tirou de cada prato, que é exatamente a trajetória que a tela grava.
      box.appendChild(
        figura(
          balanca({
            esq: inter.inicial.esq,
            dir: inter.inicial.dir,
            incognita: inter.incognita,
            pende: 'nenhum',
            title: 'Balança de dois pratos',
            desc: inter.descricao,
          }),
        ),
      );
      const etapas = document.createElement('ol');
      etapas.className = 'prova-etapas';
      // Linhas para o caminho de livro — os recipientes aos pares, os pesos do lado
      // deles aos pares, repartir —, e não para o mais curto (a meta das estrelas),
      // que pode usar o valor do recipiente e ter bem menos passos.
      const { esq, dir } = inter.inicial;
      const doLadoDosSacos = esq.sacos >= dir.sacos ? esq : dir;
      const linhas = 2 * Math.min(esq.sacos, dir.sacos) + 2 * doLadoDosSacos.pesos + 1;
      for (let i = 0; i < Math.max(linhas, inter.meta); i++)
        etapas.appendChild(document.createElement('li'));
      box.appendChild(etapas);
      break;
    }
    case 'region-select':
      box.appendChild(
        figura(
          scene(inter.backdrop, {
            viewBox: inter.viewBox,
            a11y: { title: 'Figura da questão', desc: inter.descricao },
          }),
        ),
      );
      break;
    case 'order': {
      const [de, ate] = inter.pontas.map((p) => p.replace(/[↑↓]\s*/g, '').toLowerCase());
      box.appendChild(
        el(
          'p',
          'prova-instrucao',
          `Numere os cartões de 1 (${de}) a ${inter.itens.length} (${ate}).`,
        ),
      );
      const ul = el('ul', 'prova-cartoes');
      for (const it of inter.itens) {
        const li = el('li');
        li.append(el('span', 'prova-caixinha', '(     )'), renderRichInline(it));
        ul.appendChild(li);
      }
      box.appendChild(ul);
      break;
    }
    case 'error-step': {
      const ol = el('ol', 'prova-passos');
      inter.passos.forEach((p, i) => {
        const li = el('li');
        li.append(el('span', 'prova-letra', `Passo ${i + 1}`), renderRichInline(p));
        ol.appendChild(li);
      });
      box.appendChild(ol);
      break;
    }
    case 'control-fit':
      box.appendChild(
        el('p', 'prova-linha', inter.controles.map((c) => `${c} = ________`).join('      ')),
      );
      break;
    default:
      nunca(inter, 'prova: campo de resposta');
  }
  return box;
}

/**
 * DO DIGITAL PARA O PAPEL: o que o enunciado diz sobre clicar, arrastar ou mexer num
 * controle vira o gesto equivalente com lápis. As trocas são por trecho de texto, no
 * enunciado e no comando; o que não casar fica como está — e um teste confere que não
 * sobra verbo digital em nenhuma questão do banco.
 */
const TROCAS: readonly (readonly [RegExp, string])[] = [
  // A balança: no papel não há contador nem estrelas.
  [/^Cada peça tirada conta um movimento, .* mais estrelas\.$/, ''],
  [/ Clique nesse passo\./, ' Circule esse passo.'],
  [/ — arrastando-os ou usando os botões ↑ e ↓\./, '.'],
  [/Clique em todas as /, 'Marque com um X todas as '],
  [
    /Arraste cada marcador até o ponto da reta que representa o número escrito nele\./,
    'Marque na reta, com uma seta, o ponto que representa cada número.',
  ],
  [
    /: arraste o topo de cada coluna até a altura certa\./,
    ': desenhe cada coluna até a altura certa.',
  ],
  [
    /: arraste o topo dela até a média das (\d+) colunas ser $/,
    ': redesenhe essa coluna de modo que a média das $1 colunas fique ',
  ],
  [/^Arraste os vértices $/, 'Marque os vértices '],
  [/^ até as posições que tornam/, ' nas posições que tornam'],
  [/^Arraste $/, 'Marque '],
  [/^ até que A′B′C′/, ' de modo que A′B′C′'],
  [/, e os controles abaixo da figura mudam os valores de $/, ' para certos valores de '],
  [
    /Ajuste os controles até que a reta passe pelos pontos A e B\./,
    'Quais valores de a e de b fazem a reta passar pelos pontos A e B?',
  ],
  [
    /Mova o apoio até a posição em que a régua fica em equilíbrio quando os calços forem retirados\./,
    'Em que posição da régua o apoio deve ficar para que ela fique em equilíbrio quando os calços forem retirados?',
  ],
  [/^Use o seletor para dar à coluna de $/, 'Escreva, para a coluna de '],
  [/^ um valor compatível com essa informação\./, ', um valor compatível com essa informação.'],
  [/^ o valor que mantém esse padrão\./, ', o valor que mantém esse padrão.'],
  // Qualquer verbo depois: o comando do trapézio passou a começar por "marque", e uma
  // troca presa a "construa" deixava o verbo digital passar para o papel.
  [/Usando as ferramentas disponíveis, /, 'Usando régua e compasso, '],
  // Os exploradores: no papel, a figura parada mostra um exemplo.
  [
    /^Utilize o explorador abaixo para contar, em polígonos com diferentes números de lados, quantas diagonais partem de $/,
    'Na figura, conte quantas diagonais partem de ',
  ],
  [/^Utilize o explorador abaixo para observar em quantos $/, 'Na figura, observe em quantos '],
  [/, conforme o número de lados muda\. Procure identificar/, '. Procure identificar'],
  [
    /^Utilize o explorador abaixo para avaliar a medida de um ângulo interno e a soma dos ângulos internos de polígonos regulares de 3 a 8 lados\./,
    'A figura mostra a medida de um ângulo interno e a soma dos ângulos internos de um polígono regular.',
  ],
  [
    /^\. Com o seletor, a caixa se abre até ficar planificada sobre a malha\.$/,
    '. Na figura, a caixa aparece planificada sobre a malha.',
  ],
];

export function paraOPapel(rt: RichText): RichText {
  return rt.map((n) =>
    n.t === 'text' ? { ...n, s: TROCAS.reduce((s, [re, por]) => s.replace(re, por), n.s) } : n,
  );
}

/** A resposta certa em uma linha, quando cabe numa linha. Nos outros casos, a resolução diz. */
export function chaveTexto(inst: ItemInstance): string | null {
  const inter = inst.interaction;
  switch (inter.kind) {
    case 'select-one':
      return `Alternativa ${inter.keyId}.`;
    case 'numeric-entry': {
      const u = inter.unit ? ` ${inter.unit}` : '';
      return inter.accept
        ? `Qualquer valor de ${formatBR(inter.accept.lo)} a ${formatBR(inter.accept.hi)}${u}.`
        : `${formatBR(inter.keyValue)}${u}.`;
    }
    case 'expression-entry':
      return `${inter.prefixo} ${inter.chave} — ou qualquer expressão equivalente.`;
    case 'coordinate-entry': {
      const r = inter.respostas[0];
      if (!r) return null;
      const faltam = inter.pontos.filter((p) => !p.valor);
      const pares = r
        .map((v, i) => `${faltam[i]?.nome ?? ''} = (${formatBR(v.x)}, ${formatBR(v.y)})`)
        .join('; ');
      return inter.respostas.length > 1 ? `${pares} (há outras respostas corretas).` : `${pares}.`;
    }
    case 'error-step':
      return `Passo ${inter.passoErrado + 1}.`;
    case 'order':
      return `Ordem: ${inter.ordem.map((i) => toPlainText(inter.itens[i] ?? [])).join(', ')}.`;
    case 'region-select': {
      const rot = (id: string): string => inter.regioes.find((r) => r.id === id)?.rotulo ?? id;
      return `${capitalizar(nome(inter.oQue, 'pl'))}: ${lista(inter.chave.map(rot))}.`;
    }
    case 'balance-ops':
      return `${capitalizar(umA(inter.recipiente).adj('sozinho'))} de um lado e ${conta(inter.valor, 'peso')} do outro, em ${conta(inter.meta, 'movimento')}.`;
    // Estas tres nao cabem numa linha — a figura construida, os pontos arrastados e a
    // curva ajustada so se descrevem na resolucao. Estavam saindo por um `default`
    // silencioso, que tambem engoliria uma interacao nova; agora estao escritas.
    case 'construct':
    case 'drag-points':
    case 'control-fit':
      return null;
    default:
      return nunca(inter, 'prova: gabarito em uma linha');
  }
}

export function montarProva(
  instancias: readonly ItemInstance[],
  opts: { readonly gabarito: boolean; readonly titulo?: string },
): HTMLElement {
  const prova = el('article', 'prova');
  const cab = el('header', 'prova-cabeca');
  cab.append(
    el('h1', undefined, opts.titulo ?? 'Avaliação de Matemática'),
    el(
      'p',
      'prova-campos',
      'Nome: ______________________________   Turma: ________   Data: ____/____/______',
    ),
  );
  prova.appendChild(cab);

  instancias.forEach((inst, i) => {
    const q = el('section', 'prova-questao');
    q.appendChild(el('h2', 'prova-numero', `Questão ${i + 1}`));
    if (inst.stem.length > 0) q.appendChild(renderRichText(paraOPapel(inst.stem)));
    for (const s of inst.stimuli) q.appendChild(estimulo(s));
    if (inst.comando.length > 0) {
      const c = renderRichText(paraOPapel(inst.comando));
      c.classList.add('prova-comando');
      q.appendChild(c);
    }
    q.appendChild(resposta(inst));
    prova.appendChild(q);
  });

  if (opts.gabarito) {
    const g = el('section', 'prova-gabarito');
    g.appendChild(el('h1', undefined, 'Gabarito comentado'));
    instancias.forEach((inst, i) => {
      const bloco = el('div', 'prova-gabarito-item');
      bloco.appendChild(el('h2', 'prova-numero', `Questão ${i + 1} — ${inst.title}`));
      const chave = chaveTexto(inst);
      if (chave) bloco.appendChild(el('p', 'prova-chave', chave));
      bloco.appendChild(renderRichText(inst.rationale as RichText));
      g.appendChild(bloco);
    });
    prova.appendChild(g);
  }
  return prova;
}

/**
 * Abre a página de impressão por cima do app. Fechar (ou Esc) devolve o app como
 * estava, inclusive o tema.
 */
export function abrirProvaImpressa(instancias: readonly ItemInstance[]): () => void {
  const raiz = document.documentElement;
  const temaAntes = raiz.getAttribute('data-theme');
  raiz.setAttribute('data-theme', 'light');

  const camada = el('div', 'prova-camada');
  camada.setAttribute('role', 'dialog');
  camada.setAttribute('aria-label', 'Versão para imprimir');
  const barra = el('div', 'prova-barra');
  const titulo = el(
    'strong',
    'prova-barra-titulo',
    `Versão para imprimir · ${conta(instancias.length, 'questao')}`,
  );
  const rotGab = el('label', 'prova-barra-check');
  const gab = el('input');
  gab.type = 'checkbox';
  rotGab.append(gab, document.createTextNode(' Incluir o gabarito comentado'));
  const imprimir = el('button', 'app-btn', 'Imprimir');
  imprimir.type = 'button';
  const fechar = el('button', 'app-btn-mini', 'Fechar');
  fechar.type = 'button';
  const dica = el(
    'span',
    'prova-barra-dica',
    'Se a janela de impressão não abrir, use Ctrl+P (ou ⌘+P).',
  );
  barra.append(titulo, rotGab, imprimir, fechar, dica);
  const folha = el('div', 'prova-folha');
  camada.append(barra, folha);

  const desenhar = (): void => {
    folha.replaceChildren(montarProva(instancias, { gabarito: gab.checked }));
  };
  const esc = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') fechar.click();
  };
  gab.addEventListener('change', desenhar);
  imprimir.addEventListener('click', () => {
    try {
      window.print();
    } catch {
      // página embutida sem permissão de imprimir: fica a dica do Ctrl+P
    }
  });
  fechar.addEventListener('click', () => {
    camada.remove();
    document.removeEventListener('keydown', esc);
    if (temaAntes === null) raiz.removeAttribute('data-theme');
    else raiz.setAttribute('data-theme', temaAntes);
  });
  document.addEventListener('keydown', esc);
  document.body.appendChild(camada);
  desenhar();
  return () => fechar.click();
}
